import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { MaterialIcons } from '@/components/ui/MaterialIcons'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { cn } from '@/shared/cn'

/**
 * Flag key convention: create a feature flag with this key in the admin
 * Feature Flags page. Supported metadata fields (all optional, free JSON):
 *  - message (string, required for the modal to show)
 *  - title (string, default "Aviso")
 *  - severity ('info' | 'warning' | 'critical', default 'info')
 *  - dismissible (boolean, default true)
 */
const FLAG_KEY = 'global_announcement'

type Severity = 'info' | 'warning' | 'critical'

const SEVERITY_STYLES: Record<Severity, { icon: string; color: string }> = {
  info: { icon: 'info', color: 'text-primary' },
  warning: { icon: 'warning', color: 'text-warning' },
  critical: { icon: 'error', color: 'text-error' },
}

function parseSeverity(value: unknown): Severity {
  return value === 'warning' || value === 'critical' ? value : 'info'
}

export function GlobalAnnouncementModal() {
  const { enabled, metadata, loading } = useFeatureFlag(FLAG_KEY)
  const [dismissed, setDismissed] = useState(false)

  const message =
    typeof metadata.message === 'string' ? metadata.message.trim() : ''
  const title =
    typeof metadata.title === 'string' && metadata.title.trim()
      ? metadata.title
      : 'Aviso'
  const severity = parseSeverity(metadata.severity)
  const dismissible = metadata.dismissible !== false

  const shouldShow = !loading && enabled && message.length > 0 && !dismissed

  if (!shouldShow) return null

  const handleClose = () => {
    if (!dismissible) return
    setDismissed(true)
  }

  const { icon, color } = SEVERITY_STYLES[severity]

  return (
    <Modal
      open
      onClose={handleClose}
      dismissible={dismissible}
      title={title}
      size="sm"
      zIndex={99999}
    >
      <div className="flex items-start gap-3">
        <MaterialIcons icon={icon} className={cn(color, 'text-[28px]!')} />
        <p className="text-sm whitespace-pre-wrap m-0">{message}</p>
      </div>
    </Modal>
  )
}
